import {
  createHttpMeta,
  trackHttpError,
  trackHttpResponse
} from '@/config/insight/tracking-http.insight'
import Response from '@/modules/core/models/Response'
import ServiceError from '@/modules/core/models/errors/ServiceError'
import DXPMockService from '@/modules/core/services/DXPMockService'
import { getMDEnvironmentRootPath } from '@/utils/envUtils'

export interface RequestConfig<D = any> {
  url?: string
  method?: string
  baseURL?: string
  headers?: any
  params?: any
  data?: D
  mock?: boolean
  signal?: AbortSignal
}

const ROOT_PATH = getMDEnvironmentRootPath()

const HEADERS = {
  'Ocp-Apim-Subscription-Key': import.meta.env.VITE_API_SUBSCRIPTION_KEY,
  'Content-Type': 'application/json',
  'Access-Control-Allow-Origin': '*'
  // 'Subscription-key': subscriptionKey,
  // Authorization: `Subscription-key ${subscriptionKey}`,
}

async function parseResponse(responseJSON: any) {
  try {
    return await responseJSON.json()
  } catch (error) {
    return ''
  }
}

export default class MiddlewareService {
  static async fetchCall({ url, method, data, signal }: RequestConfig): Promise<Response> {
    const connectionString = import.meta.env.VITE_KEY_INSIGHTS
    const httpMeta = createHttpMeta({
      method: method ?? 'GET',
      url: `${ROOT_PATH}${url}`,
      request: data
    })

    try {
      const config = {
        method,
        headers: HEADERS,
        body: JSON.stringify(data),
        signal
      }
      const responseJSON = await fetch(`${ROOT_PATH}${url}`, config)
      const response = await parseResponse(responseJSON)

      if (!responseJSON.ok) {
        const error = new ServiceError({
          message: response?.message ?? response?.title,
          code: response?.code ?? response?.status ?? responseJSON.status,
          serviceName: url,
          stack: response?.errors
        })

        if (connectionString) {
          trackHttpError(connectionString, httpMeta, error, {
            trackException: false,
            properties: {
              serviceName: url,
              serviceType: 'Middleware'
            }
          })
        }

        throw error
      }

      if (connectionString) {
        trackHttpResponse(
          connectionString,
          httpMeta,
          { status: responseJSON.status, data: response },
          {
            properties: {
              serviceName: url,
              serviceType: 'Middleware'
            }
          }
        )
      }

      return new Response({ data: response })
    } catch (error: any) {
      const isAborted = error.name === 'AbortError'
      const serviceError = new ServiceError({
        ...error,
        message: error.message,
        code: error.code,
        serviceName: url,
        payload: data
      })

      if (!isAborted && connectionString) {
        trackHttpError(connectionString, httpMeta, serviceError, {
          trackException: true,
          properties: {
            serviceName: url,
            serviceType: 'Middleware',
            isAborted
          }
        })
      }

      return new Response({ data: serviceError, error: true, aborted: isAborted })
    }
  }

  static async get(url: string, config?: RequestConfig) {
    if (config?.mock) {
      return DXPMockService.fetchCall({ ...config, url, method: 'GET' })
    }

    delete config?.mock
    return this.fetchCall({ ...config, url, method: 'GET' })
  }

  static async post(url: string, data: unknown, config?: RequestConfig) {
    if (config?.mock) {
      return DXPMockService.fetchCall({ ...config, url, method: 'POST' })
    }

    delete config?.mock
    return this.fetchCall({ ...config, url, method: 'POST', data })
  }

  static async delete(url: string, config?: RequestConfig) {
    if (config?.mock) {
      return DXPMockService.fetchCall({ ...config, url, method: 'DELETE' })
    }

    delete config?.mock
    return this.fetchCall({ ...config, url, method: 'DELETE' })
  }
}
